import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { FileSignature, Send, CheckCircle } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const fmt$ = (v) => `$${Math.round(v || 0).toLocaleString()}`;

const ageStyle = (a) => a <= 7 ? { color: C.green, label: 'On track' }
  : a <= 14 ? { color: C.amber, label: 'Chase' }
  : a <= 30 ? { color: '#D97706', label: 'Slow' }
  : { color: C.red, label: 'Critical' };

const Overline = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{children}</p>
);

export default function DischargeAgingPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get('/claims/discharges/aging').then(r => setData(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const chase = async (item) => {
    setBusy(item.claim_id);
    try {
      await api.post(`/claims/${item.claim_id}/discharge/chase`);
      toast.success(`Reminder sent to ${item.broker_name || 'the servicing broker'} for ${item.claim_reference}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send reminder'); }
    setBusy(null);
  };

  const markReturned = async (item) => {
    setBusy(item.claim_id);
    try {
      await api.post(`/claims/${item.claim_id}/discharge/mark-returned`);
      toast.success(`${item.claim_reference} — discharge returned, payment released to the next run`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not mark returned'); }
    setBusy(null);
  };

  if (!data) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-sm" />)}</div>;

  return (
    <div className="space-y-5" data-testid="discharge-aging-page">
      <div>
        <h2 className="text-xl font-medium tracking-tight" style={{ color: C.navy }}>Discharge Aging Board</h2>
        <p className="text-sm mt-0.5" style={{ color: C.slate }}>Every outstanding signed discharge by age — the slowest ones need a personal follow-up before settlement money can move.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[['Outstanding discharges', data.count, C.navy, `${fmt$(data.held_total)} settlement held`],
          ['Average age', `${data.avg_age}d`, data.avg_age > 14 ? C.amber : C.blue, 'since discharge issued'],
          ['Over 30 days', data.buckets['30+'], data.buckets['30+'] > 0 ? C.red : C.green, 'critical — call the broker'],
          ['8–30 days', data.buckets['8-14'] + data.buckets['15-30'], C.amber, 'ARIA is chasing weekly']].map(([l, v, color, d]) => (
          <Card key={l} className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
            <Overline>{l}</Overline>
            <p className="text-3xl font-light tracking-tight mt-1" style={{ color, fontVariantNumeric: 'tabular-nums' }} data-testid={`discharge-stat-${l.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{v}</p>
            <p className="text-[10px] mt-1" style={{ color: C.slate }}>{d}</p>
          </Card>
        ))}
      </div>

      <Card className="rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="discharge-aging-table">
        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] uppercase tracking-wider border-b" style={{ color: C.slate, borderColor: 'rgba(136,150,168,0.15)' }}>
          <span className="col-span-3 font-semibold">Claim</span>
          <span className="col-span-2 font-semibold">Broker</span>
          <span className="col-span-2 font-semibold">Age</span>
          <span className="col-span-1 font-semibold text-right">Held</span>
          <span className="col-span-2 font-semibold">Chased</span>
          <span className="col-span-2 font-semibold text-right">Actions</span>
        </div>
        {data.items.length === 0 ? (
          <div className="py-10 text-center">
            <FileSignature className="w-7 h-7 mx-auto mb-2" style={{ color: C.slate }} />
            <p className="text-sm" style={{ color: C.slate }}>No outstanding discharges — every settlement is cleared for payment.</p>
          </div>
        ) : data.items.map(item => {
          const st = ageStyle(item.age_days);
          return (
            <div key={item.claim_id} className="grid grid-cols-12 gap-2 items-center px-4 py-3 border-b hover:bg-[#F5F7FA]" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`discharge-row-${item.claim_reference}`}>
              <div className="col-span-3 min-w-0">
                <span className="font-mono text-xs" style={{ color: C.navy }}>{item.claim_reference}</span>
                <p className="text-[10px] truncate" style={{ color: C.slate }}>{item.business_name || item.claimant_name}</p>
              </div>
              <div className="col-span-2 min-w-0">
                <p className="text-xs truncate" style={{ color: C.navy }}>{item.broker_name || '—'}</p>
                <p className="text-[10px] truncate" style={{ color: C.slate }}>{item.broker_email}</p>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <span className="font-mono text-xs font-semibold" style={{ color: st.color }}>{item.age_days}d</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${st.color}12`, color: st.color }}>{st.label}</span>
              </div>
              <div className="col-span-1 text-right font-mono text-xs" style={{ color: C.navy }}>{fmt$(item.paid_amount)}</div>
              <div className="col-span-2 text-[11px]" style={{ color: C.slate }}>
                {item.chase_count > 0 ? `${item.chase_count}× · last ${(item.last_chased_at || '').slice(0, 10)}` : 'never'}
              </div>
              <div className="col-span-2 flex justify-end gap-1.5">
                <Button size="sm" disabled={busy === item.claim_id} onClick={() => chase(item)}
                  className="text-[11px] rounded-sm text-white h-7" style={{ background: C.blue }}
                  data-testid={`discharge-chase-${item.claim_reference}`}>
                  <Send className="w-3 h-3 mr-1" /> Remind
                </Button>
                <Button size="sm" variant="outline" disabled={busy === item.claim_id} onClick={() => markReturned(item)}
                  className="text-[11px] rounded-sm h-7" style={{ color: C.green, borderColor: `${C.green}40` }}
                  data-testid={`discharge-returned-${item.claim_reference}`}>
                  <CheckCircle className="w-3 h-3 mr-1" /> Returned
                </Button>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}
